import * as React from 'react';
import {FC, useEffect, useState} from 'react';
import {get} from 'lodash';
import {Loader, Pagination, Table, TableHeader} from '@flexe/ui-components';
import {Link} from 'react-router-dom';
import {format as formatDate} from 'date-fns';
import Filters from '../shared-v2/Filters';
import {Filter, FilterOption, FilterType, FilterValue} from '../../shared/CommonInterfaces';
import {dateFormat, OrdersPageError} from './OutboundOrdersV2';
import OutboundOrdersService from './OutboundOrdersService';
import {OrderStatus, OrderStatusDisplay, OrderType, OutboundOrder} from './OutboundOrdersInterfaces';
import {PageSize} from './generic/PageSizeInput';
import {extractNewFilters, FilterInfo, getInputFilterValue, initialFilters} from './helpers/OutboundOrdersFilters';
import ReservationsService from './ReservationsService';

interface Props {
  companyId: number;
  outboundOrdersService: OutboundOrdersService;
  pageSize: PageSize;
  setErrors: (v: OrdersPageError) => void;
}

interface OrdersTableInfo {
  continuationTokens: string[];
  orders: OutboundOrder[];
  totalOrderCount: number;
  filters: FilterInfo;
  currentPage: number;
  reservationOptions: FilterOption[];
}
const initialOrdersTableInfo: OrdersTableInfo = {
  continuationTokens: [],
  orders: [],
  totalOrderCount: 0,
  filters: initialFilters,
  currentPage: 1,
  reservationOptions: []
};

function getFilters(ordersTableInfo: OrdersTableInfo, companyId: number): Filter[] {
  const inputFilters = ordersTableInfo.filters.inputFilters;
  return [
    {
      displayName: 'External Order ID',
      key: 'externalIds',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'externalIds'),
      allowMultiple: true
    },
    {
      displayName: 'FLEXE Order ID',
      key: 'ids',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'ids'),
      allowMultiple: true
    },
    {
      displayName: 'Order Status',
      key: 'state',
      type: FilterType.Dropdown,
      options: [
        {
          displayName: 'Choose a status...',
          value: ''
        },
        {
          displayName: OrderStatusDisplay[OrderStatus.open],
          value: OrderStatus.open
        },
        {
          displayName: OrderStatusDisplay[OrderStatus.closed],
          value: OrderStatus.closed
        }
      ],
      value: getInputFilterValue(inputFilters, 'state')
    },
    {
      displayName: 'Order Type',
      key: 'orderType',
      type: FilterType.Dropdown,
      options: [
        {
          displayName: 'Choose an order type...',
          value: ''
        },
        {
          displayName: OrderType.distribution,
          value: OrderType.distribution
        },
        {
          displayName: OrderType.ecommerce,
          value: OrderType.ecommerce
        }
      ],
      value: getInputFilterValue(inputFilters, 'orderType')
    },
    {
      displayName: 'Reservation',
      key: 'reservationIds',
      type: FilterType.Dropdown,
      options: ordersTableInfo.reservationOptions,
      allowMultiple: true,
      value: getInputFilterValue(inputFilters, 'reservationIds')
    },
    {
      displayName: 'Created (Exact)',
      key: 'createdAt',
      type: FilterType.Date,
      value: getInputFilterValue(inputFilters, 'createdAt')
    },
    {
      displayName: 'Created Starting',
      key: 'createdAtFrom',
      type: FilterType.Date,
      value: getInputFilterValue(inputFilters, 'createdAtFrom')
    },
    {
      displayName: 'Created Ending',
      key: 'createdAtTo',
      type: FilterType.Date,
      value: getInputFilterValue(inputFilters, 'createdAtTo')
    },
    {
      displayName: 'Updated Starting',
      key: 'updatedAtFrom',
      type: FilterType.Date,
      value: getInputFilterValue(inputFilters, 'updatedAtFrom')
    },
    {
      displayName: 'Updated Ending',
      key: 'updatedAtTo',
      type: FilterType.Date,
      value: getInputFilterValue(inputFilters, 'updatedAtTo')
    },
    {
      displayName: 'Issues Only',
      key: 'hasErrors',
      type: FilterType.Present,
      value: getInputFilterValue(inputFilters, 'hasErrors')
    },
    {
      displayName: 'Labels',
      key: 'labels',
      type: FilterType.LabelSearch,
      allowMultiple: true,
      value: getInputFilterValue(inputFilters, 'labels'),
      defaultKey: companyId === 3379 ? 'shopify_order_id' : 'shopify_order_number'
    }
  ];
}

async function getPageOfOrders(
  pageSize: PageSize,
  setErrors: (v: OrdersPageError) => void,
  outboundOrdersService: OutboundOrdersService,
  currentOrdersTableInfo: OrdersTableInfo
): Promise<OrdersTableInfo> {
  const newOrdersTableInfo: OrdersTableInfo = {...currentOrdersTableInfo};

  if (!outboundOrdersService) {
    setErrors({errorType: 'UnexpectedFailure'});
    return newOrdersTableInfo;
  }

  let ordersError: OrdersPageError | undefined;
  const reservationErrors: string[] = [];
  const currentPage = newOrdersTableInfo.currentPage;

  // TODO stop loading reservations at all; just pass them from ruby
  const [ordersResponse, reservationOptions] = await Promise.all([
    outboundOrdersService.getOrders(
      newOrdersTableInfo.filters.apiFilters,
      currentPage > 1 ? newOrdersTableInfo.continuationTokens[currentPage - 2] : null,
      pageSize,
      null,
      {returnAllErrors: true}
    ),
    loadReservations(reservationErrors)
  ]);

  if (!ordersResponse) {
    newOrdersTableInfo.orders = [];
    newOrdersTableInfo.totalOrderCount = 0;

    ordersError = {errorType: 'UnexpectedFailure'};
  } else if (ordersResponse.errors && ordersResponse.errors.length > 0) {
    newOrdersTableInfo.orders = [];
    newOrdersTableInfo.totalOrderCount = 0;

    ordersError = {errorType: 'ApiResponse', details: outboundOrdersService.processErrors(ordersResponse.errors)};
  } else {
    const newContinuationTokens: string[] = newOrdersTableInfo.continuationTokens;
    // TODO this returning an array is bizarre
    const newToken = [get(ordersResponse, 'continuationToken')][0];
    if (currentPage > newContinuationTokens.length) {
      newContinuationTokens.push(newToken);
    } else {
      newContinuationTokens[currentPage - 1] = newToken;
    }
    newOrdersTableInfo.continuationTokens = newContinuationTokens;
    newOrdersTableInfo.orders = get(ordersResponse, 'outboundOrders') || ([] as OutboundOrder[]);
    newOrdersTableInfo.totalOrderCount = get(ordersResponse, 'total');
  }

  newOrdersTableInfo.reservationOptions = reservationOptions;

  // TODO when we remove reservation retrieval, this clunkiness will go away. For now, just combine all errors
  if (ordersError) {
    const combinedDetails =
      reservationErrors.length > 0 || ordersError.details
        ? reservationErrors.concat(ordersError.details || [])
        : undefined;
    setErrors({errorType: ordersError.errorType, details: combinedDetails});
  } else if (reservationErrors.length > 0) {
    setErrors({errorType: 'ApiResponse', details: reservationErrors});
  } else {
    setErrors({errorType: 'None'});
  }
  return newOrdersTableInfo;
}

async function loadReservations(errors: string[]): Promise<FilterOption[]> {
  const reservationsService = new ReservationsService();
  const reservationData = await reservationsService.getReservations();
  if (reservationData && reservationData.data && reservationData.data.reservations) {
    const placeholderOption: FilterOption[] = [
      {
        displayName: 'Choose a reservation...',
        value: ''
      }
    ];

    const options: FilterOption[] = reservationData.data.reservations
      .filter((res) => {
        return res.txn_state !== 'new';
      })
      .map((res) => {
        return {
          displayName: `${res.id}: ${res.warehouse.name}, ${res.warehouse.address.locality}`,
          value: res.id.toString()
        };
      });

    const allOptions = placeholderOption.concat(options).sort((a, b) => {
      return a.value > b.value ? 1 : a.value < b.value ? -1 : 0;
    });
    return allOptions;
  } else {
    errors.push(
      'Error fetching reservations. Filtering by reservations will be unavailable. Please check your connection and ' +
        'refresh. Contact Flexe if the error persists.'
    );
    return [];
  }
}

function buildOrdersRows(ordersTableInfo: OrdersTableInfo) {
  if (!ordersTableInfo || !ordersTableInfo.orders) {
    return [];
  }
  return ordersTableInfo.orders.map((order) => {
    const url = `/s/fulfillment/orders/${order.id}`;
    return [
      order.hasErrors ? <i className="fa fa-exclamation-triangle red3" /> : null,
      <Link to={url}>{order.id}</Link>,
      <div>{order.externalId}</div>,
      <span className={`label label-default ${order.state}`}>{OrderStatusDisplay[order.state]}</span>,
      <div>{getReservationToDisplay(order.requestedReservationIds, order.plannedReservationIds, ordersTableInfo)}</div>,
      <div>{order.recipient.name}</div>,
      <div>{order.recipient.address.line1}</div>,
      <div>{order.createdAt ? formatDate(order.createdAt, dateFormat) : null}</div>,
      <Link className="pull-right" to={url}>
        <i className="fa fa-chevron-right" />
      </Link>
    ];
  });
}

function getReservationToDisplay(
  requestedReservationIds: string[],
  plannedReservationIds: string[],
  ordersTableInfo: OrdersTableInfo
) {
  requestedReservationIds = requestedReservationIds || [];
  plannedReservationIds = plannedReservationIds || [];
  const reservations = new Set([...requestedReservationIds, ...plannedReservationIds]);

  if (reservations.size > 1) {
    return <i>multiple</i>;
  } else if (reservations.size === 1) {
    return getDisplayNameForReservation(reservations.values().next().value, ordersTableInfo);
  } else {
    return '-';
  }
}

function getDisplayNameForReservation(reservationId: string, ordersTableInfo: OrdersTableInfo) {
  let displayName;
  ordersTableInfo.reservationOptions.forEach((value) => {
    if (value.value === reservationId) {
      displayName = value.displayName;
    }
  });
  // fallback to id just in case something went wrong instead of displaying nothing
  return displayName ? displayName : reservationId;
}

function tableHeaders(
  ordersTableInfo: OrdersTableInfo,
  pageSize: PageSize,
  changePage: (page: number) => void
): TableHeader[] {
  return [
    {
      className: 'issue-header',
      element: <i className="fa fa-exclamation-triangle red3" />
    },
    {className: 'id-header', element: 'FLEXE Order ID'},
    {element: 'External Order ID'},
    {element: 'Status'},
    {element: 'Reservation'},
    {element: 'Name'},
    {element: 'Address'},
    {element: 'Created'},
    {
      className: 'pagination-header',
      element: (
        <Pagination
          page={ordersTableInfo.currentPage}
          pageSize={pageSize}
          paginationHandler={(page) => changePage(page)}
          totalCount={ordersTableInfo.totalOrderCount}
        />
      )
    }
  ];
}

const OutboundOrdersTable: FC<Props> = (props) => {
  const [ordersTableInfo, setOrdersTableInfo] = useState<OrdersTableInfo>(initialOrdersTableInfo);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Since pageSize is required, this handles initial load as well
  useEffect(() => {
    // Make sure we keep filters when changing page size
    const newOrdersTableInfo: OrdersTableInfo = {...initialOrdersTableInfo, filters: ordersTableInfo.filters};
    loadOrders(newOrdersTableInfo);
  }, [props.pageSize]);

  const loadOrders = (newOrdersTableInfo: OrdersTableInfo) => {
    setIsLoading(true);

    getPageOfOrders(props.pageSize, props.setErrors, props.outboundOrdersService, newOrdersTableInfo)
      .then((result) => {
        setOrdersTableInfo(result);
        setIsLoading(false);
      })
      .catch((_) => {
        // TODO Display the error and maybe add a retry button too (so you don't have to reload and start over)
        setOrdersTableInfo(newOrdersTableInfo); // TODO can likely remove. Just be safe for now
        setIsLoading(false);
      });
  };

  const handleFilterChange = (newFilters: FilterValue[]) => {
    // Changing a filter means we have to start fresh
    const newOrdersTableInfo: OrdersTableInfo = {...initialOrdersTableInfo, filters: extractNewFilters(newFilters)};
    loadOrders(newOrdersTableInfo);
  };

  const changePage = (newPage: number) => {
    // Page changes mean we can keep all the current data, but load a different page
    const newOrdersTableInfo: OrdersTableInfo = {...ordersTableInfo, currentPage: newPage};
    loadOrders(newOrdersTableInfo);
  };

  if (isLoading) {
    return <Loader loading={isLoading} />;
  }

  return (
    <>
      <div className="row filters">
        <Filters
          filters={getFilters(ordersTableInfo, props.companyId)}
          filterChangeHandler={(newFilters) => handleFilterChange(newFilters)}
        />
      </div>
      {ordersTableInfo.orders.length === 0 ? (
        <p>No orders matching the current filters were found.</p>
      ) : (
        <Table
          tableClass="table-striped"
          tableData={{
            headers: tableHeaders(ordersTableInfo, props.pageSize, changePage),
            rows: buildOrdersRows(ordersTableInfo)
          }}
        />
      )}
    </>
  );
};
export default OutboundOrdersTable;
